import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, MessageSquare, Plus, History } from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { cn } from '../../lib/utils';

// Helper function to strip markdown formatting for preview text
const stripMarkdown = (text) => {
  if (!text) return '';
  return text
    .replace(/(\*\*|__)(.*?)\1/g, '$2') // Bold
    .replace(/(\*|_)(.*?)\1/g, '$2') // Italic
    .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1') // Links
    .replace(/`{1,3}[^`\n]+`{1,3}/g, '') // Code
    .replace(/#{1,6}\s?/g, '') // Headers
    .replace(/>\s?/g, '') // Blockquotes
    .replace(/[-*+]\s/g, '') // Lists
    .replace(/\n/g, ' ') // Newlines
    .trim();
};

const Sidebar = ({ isSidebarOpen, toggleSidebar, conversations, activeConversationId, setActiveConversationId, handleNewChat }) => {
  const location = useLocation();
  const groupedConversations = Object.entries(conversations).sort(
    ([dateA], [dateB]) => new Date(dateB) - new Date(dateA)
  );

  return (
    <>
      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={toggleSidebar}
        />
      )}
      
      {/* Sidebar */}
      <aside className={cn(
        "fixed top-16 left-0 bottom-0 w-72 border-r bg-card z-40 flex flex-col transition-transform duration-300 ease-in-out",
        "md:translate-x-0",
        isSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
      )}>
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-muted-foreground" />
            <h2 className="font-semibold">Conversations</h2>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={toggleSidebar}
            className="md:hidden"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* New Chat Button */}
        <div className="p-3">
          <Button 
            className="w-full justify-start gap-2" 
            variant={location.pathname === "/" ? "secondary" : "outline"}
            onClick={() => {
              handleNewChat();
              toggleSidebar();
            }}
          >
            <Plus className="h-4 w-4" />
            New Chat
          </Button>
        </div>

        {/* Conversations List */}
        <ScrollArea className="flex-1 px-3">
          {groupedConversations.length > 0 ? (
            <div className="space-y-4 pb-4">
              {groupedConversations.map(([date, convos]) => (
                <div key={date} className="space-y-1">
                  <h3 className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {formatDate(date)}
                  </h3>
                  <div className="space-y-1">
                    {convos.map((conversation) => {
                      const isActive = activeConversationId === conversation.id || 
                        location.pathname === `/conversations/${conversation.id}`;
                      const lastMessage = conversation.messages[conversation.messages.length - 1]?.message;
                      const strippedMessage = stripMarkdown(lastMessage || '');
                      const preview = strippedMessage ? strippedMessage.substring(0, 35) + (strippedMessage.length > 35 ? '...' : '') : 'No messages yet';
                      
                      return (
                        <Link
                          key={conversation.id}
                          to={`/conversations/${conversation.id}`}
                          onClick={() => {
                            setActiveConversationId(conversation.id);
                            toggleSidebar();
                          }}
                        >
                          <div className={cn(
                            "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer",
                            isActive 
                              ? "bg-primary/10 text-primary" 
                              : "hover:bg-muted text-foreground"
                          )}>
                            <MessageSquare className={cn(
                              "h-4 w-4 flex-shrink-0",
                              isActive ? "text-primary" : "text-muted-foreground"
                            )} />
                            <span className="text-sm truncate">{preview}</span>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <MessageSquare className="h-10 w-10 text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">No conversations yet</p>
              <p className="text-xs text-muted-foreground/70 mt-1">Start a new chat to begin</p>
            </div>
          )}
        </ScrollArea>
      </aside>
    </>
  );
};

// Helper function to format dates nicely
function formatDate(dateString) {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}

export default Sidebar;
